import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const YearClose = () => {
  const navigate = useNavigate();
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY,     setSelectedFY]     = useState(null);
  const [checklist,      setChecklist]      = useState(null);
  const [balances,       setBalances]       = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [closing,        setClosing]        = useState(false);
  const [activeTab,      setActiveTab]      = useState("checklist"); // checklist | balances | adjust

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) { fetchChecklist(); fetchBalances(); } }, [selectedFY]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      const years = res.details || [];
      setFinancialYears(years);
      const cur = years.find((y) => y.is_current === 1);
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
  };

  const fetchChecklist = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getYearCloseChecklist({ financial_year_id: selectedFY.value });
    if (res.status === "OK") setChecklist(res.details);
    setLoading(false);
  };

  const fetchBalances = async () => {
    const res = await ajaxAccounting.getTrialBalance({ financial_year_id: selectedFY.value });
    if (res.status === "OK") {
      // The trial balance returns `entries`; reading accounts/list left this
      // tab empty on a year that had a full ledger behind it.
      const accs = res.details?.entries || res.details?.accounts || res.details?.list || [];
      setBalances(accs);
    }
  };

  const handleClose = async () => {
    if (!window.confirm("Close this financial year? This cannot be undone after final confirmation.")) return;
    setClosing(true);
    const res = await ajaxAccounting.closeFinancialYear({ id: selectedFY.value });
    if (res.status === "OK") { toast.success("Financial year closed successfully!"); fetchChecklist(); }
    else toast.error(res.message || "Failed to close financial year.");
    setClosing(false);
  };

  const fyOptions = financialYears.map((y) => ({ value: y.id, label: y.name + (y.is_current ? " (Current)" : "") }));

  /**
   * Where each check can be dealt with. A checklist that only says a thing is
   * wrong leaves the bursar hunting for it; `fix` is what the button says and
   * `link` is where it goes.
   *
   * Keyed on the `check` the API returns. The list used to be hand-written
   * against flat fields — checklist.unposted_journals and the like — that the
   * endpoint has never sent: every one of them read as undefined, `undefined
   * === 0` is false, and so every check showed red no matter what the books
   * actually said.
   */
  const CHECK_ACTIONS = {
    no_draft_journals:      { link: "/accounting/journal?status=draft",              fix: "Post them" },
    no_draft_vouchers:      { link: "/accounting/vouchers?status=draft",             fix: "Post them" },
    no_draft_invoices:      { link: "/accounting/invoices?status=draft",             fix: "Review" },
    no_pending_approvals:   { link: "/accounting/approvals",                         fix: "Approve" },
    trial_balance_balanced: { link: "/accounting/reports/trial-balance",             fix: "Open trial balance" },
    payroll_complete:       { link: "/accounting/payroll",                           fix: "Open payroll" },
    vat_returns_filed:      { link: "/accounting/tax",                               fix: "Generate returns" },
  };

  const checkItems = (checklist?.checks || []).map((c) => ({
    key:   c.check,
    label: c.label,
    count: c.count,
    message: c.message,
    ok:    !!c.passed,
    ...(CHECK_ACTIONS[c.check] || {}),
  }));

  const canClose = checkItems.length > 0 && checkItems.every((i) => i.ok || i.warn);
  const blockers = checkItems.filter((i) => !i.ok && !i.warn);

  // Group balances
  const grouped = {};
  (Array.isArray(balances) ? balances : []).forEach((acc) => {
    const t = acc.type || "other";
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(acc);
  });

  const typeColors = { asset:"#6366f1", liability:"#ef4444", equity:"#f59e0b", revenue:"#10b981", expense:"#8b5cf6" };

  return (
    <AppContainer
      title="Financial Year Closing"
      previousPageTitle="Financial Year"
      description="Review your financials before closing the year. Check the pre-close checklist, review account balances, make any adjustments, then close."
      actions={[
        { label: "Post Closing Entries", icon: "fa-solid fa-book-open", className: "btn btn-outline-secondary",
          onClick: async () => {
            const res = await ajaxAccounting.postYearCloseEntries({ financial_year_id: selectedFY?.value });
            if (res.status === "OK") toast.success("Closing entries posted!"); else toast.error(res.message);
          }},
        { label: closing ? "Closing..." : "Close Financial Year", icon: "fa-solid fa-lock",
          className: `btn ${canClose ? "btn-danger" : "btn-secondary"}`,
          onClick: handleClose, disabled: !canClose || closing },
      ]}
    >
      <Toaster />

      {/* FY Selector */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year to Close</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY}
              placeholder="Select financial year" isSearchable />
          </div>
          {selectedFY && checklist && (
            <div className="col-lg-8">
              <div className="d-flex gap-2 flex-wrap">
                {blockers.length === 0
                  ? <span className="badge bg-success p-2 tx-12"><i className="fa-solid fa-check me-1"></i>Ready to close</span>
                  : <span className="badge bg-danger p-2 tx-12"><i className="fa-solid fa-triangle-exclamation me-1"></i>{blockers.length} blocking issue(s) must be resolved first</span>
                }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key:"checklist", label:"Pre-Close Checklist", icon:"fa-clipboard-check" },
          { key:"balances",  label:"Account Balances",    icon:"fa-table-list" },
          { key:"adjust",    label:"Adjusting Entries",   icon:"fa-pen-to-square" },
        ].map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${activeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* CHECKLIST TAB */}
      {activeTab === "checklist" && (
        <div className="card custom-card p-4">
          {loading ? (
            <div className="text-center py-4 tx-muted"><i className="fa-solid fa-spinner fa-spin"></i> Loading checklist...</div>
          ) : !checklist ? (
            <div className="text-center py-4 tx-muted">Select a financial year to view the checklist.</div>
          ) : (
            <>
              <h6 className="fw-semibold mb-3">Pre-Close Checklist</h6>
              <div className="row g-2">
                {checkItems.map((item) => (
                  <div key={item.key} className="col-12">
                    <div className="d-flex align-items-center justify-content-between p-3 rounded"
                      style={{ border: `1px solid ${item.ok ? "#d1fae5" : item.warn ? "#fef3c7" : "#fee2e2"}`,
                               background: item.ok ? "#f0fdf4" : item.warn ? "#fffbeb" : "#fef2f2" }}>
                      <div className="d-flex align-items-center gap-3">
                        <i className={`fa-solid ${item.ok ? "fa-circle-check tx-success" : item.warn ? "fa-triangle-exclamation tx-warning" : "fa-circle-xmark tx-danger"}`}
                          style={{ fontSize: "1.2rem" }}></i>
                        <div>
                          <div className="fw-semibold tx-14">{item.label}</div>
                          {/* The API's own words. It says what is actually
                              wrong — and, when nothing is, says so — which
                              beats "0 record(s)" under a green tick. */}
                          {item.message && (
                            <div className="tx-12 tx-muted">{item.message}</div>
                          )}
                          {!item.ok && item.count > 0 && (
                            <div className="tx-12 tx-muted">{item.count} record(s)</div>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {item.warn && item.count > 0 && (
                          <span className="badge bg-warning">Review recommended</span>
                        )}
                        {/* Anything not yet clear gets a way through to it. */}
                        {!item.ok && item.link && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => navigate(item.link)}
                          >
                            {item.fix || "Fix"} <i className="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        )}
                        {item.ok && item.link && item.count > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-light"
                            onClick={() => navigate(item.link)}
                            title={`Open ${item.label.toLowerCase()}`}
                          >
                            View <i className="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {blockers.length === 0 && (
                <div className="alert alert-success mt-4 d-flex align-items-center gap-2">
                  <i className="fa-solid fa-circle-check" style={{ fontSize: "1.3rem" }}></i>
                  <div>
                    <strong>All critical checks passed.</strong> You may proceed to close this financial year.
                    Review the Account Balances tab to verify figures before closing.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* BALANCES TAB */}
      {activeTab === "balances" && (
        <div className="card custom-card p-4">
          <div className="d-flex justify-content-between mb-3">
            <h6 className="fw-semibold mb-0">Account Balances Before Closing</h6>
            <span className="tx-12 tx-muted">Review these figures. Make adjusting entries if needed before closing.</span>
          </div>
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-4 tx-muted">Select a financial year to view balances.</div>
          ) : (
            Object.entries(grouped).map(([type, accs]) => {
              // The trial balance sends total_debit / total_credit / account_name.
              // Reading debit_balance / credit_balance / name — which it has
              // never sent — drew a table of blank names and dashes over a year
              // with a full ledger behind it.
              const totalDr = accs.reduce((s, a) => s + (parseFloat(a.total_debit) || 0), 0);
              const totalCr = accs.reduce((s, a) => s + (parseFloat(a.total_credit) || 0), 0);
              return (
                <div key={type} className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2 p-2 rounded"
                    style={{ background: (typeColors[type] || "#6b7280") + "15", borderLeft: `4px solid ${typeColors[type] || "#6b7280"}` }}>
                    <strong className="text-capitalize">{type} Accounts</strong>
                    <span className="tx-12">
                      Dr: <strong>{functions.formatCurrency(totalDr)}</strong> &nbsp;|&nbsp;
                      Cr: <strong>{functions.formatCurrency(totalCr)}</strong>
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Code</th><th>Account Name</th>
                          <th className="text-end">Debit (Dr)</th>
                          <th className="text-end">Credit (Cr)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accs.map((acc, i) => (
                          <tr key={i}>
                            <td><code className="tx-12">{acc.account_code}</code></td>
                            <td>{acc.account_name}</td>
                            <td className="text-end tx-success">
                              {parseFloat(acc.total_debit) > 0 ? functions.formatCurrency(acc.total_debit) : "—"}
                            </td>
                            <td className="text-end tx-danger">
                              {parseFloat(acc.total_credit) > 0 ? functions.formatCurrency(acc.total_credit) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ADJUSTING ENTRIES TAB */}
      {activeTab === "adjust" && (
        <div className="card custom-card p-4 text-center py-5">
          <i className="fa-solid fa-pen-to-square" style={{ fontSize: "2.5rem", color: "#4f46e5", opacity: 0.5 }}></i>
          <h6 className="mt-3 fw-semibold">Make Adjusting Entries</h6>
          <p className="tx-13 tx-muted mb-4">
            Create adjusting journal entries to correct account balances before closing.
            Use General Journal entries to post accruals, deferrals, or corrections.
          </p>
          <Link to="/accounting/journal/new?type=general" className="btn btn-primary">
            <i className="fa-solid fa-plus me-1"></i>New Adjusting Entry
          </Link>
          <Link to="/accounting/journal?status=posted" className="btn btn-outline-secondary ms-2">
            <i className="fa-solid fa-list me-1"></i>View All Entries
          </Link>
        </div>
      )}
    </AppContainer>
  );
};

export default YearClose;
